import { Link } from "react-router-dom";
import { Rocket, Lock, Clock, Send, Shield, Sparkles, Package, ChevronRight, ArrowRight } from "lucide-react";

import { _t } from "@/lib/i18n";
function Nav() {
  return (
    <header className="absolute top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity">
          <img src="/images/img1.png" alt={_t("Time Capsule")} className="w-7 h-7 rounded-md" />
          <div className="font-display text-lg text-white/90 tracking-wide">{_t("Time Capsule")}</div>
        </Link>
        <nav className="hidden md:flex items-center gap-1 text-sm">
          <Link to="/come-funziona" className="px-3 py-2 text-white/70 hover:text-white">{_t("Come funziona")}</Link>
          <Link to="/prezzi" className="px-3 py-2 text-white/70 hover:text-white">{_t("Prezzi")}</Link>
          <Link to="/sicurezza" className="px-3 py-2 text-white/70 hover:text-white">{_t("Sicurezza")}</Link>
          <Link to="/faq" className="px-3 py-2 text-white/70 hover:text-white">{_t("FAQ")}</Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="landing-login" className="btn-ghost">{_t("Accedi")}</Link>
          <Link to="/register" data-testid="landing-register" className="btn-launch">{_t("Inizia gratis")}</Link>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="max-w-7xl mx-auto px-6 py-16 border-t border-white/5">
      <div className="grid md:grid-cols-4 gap-8 mb-10">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <Rocket className="w-5 h-5 text-[#FF5722] -rotate-45" />
            <div className="font-display text-xl text-white">{_t("Time Capsule")}</div>
          </div>
          <p className="text-sm text-white/50 leading-relaxed">{_t("Il futuro è già qui. Conserva oggi ciò che conta.")}</p>
        </div>
        <div>
          <div className="text-white text-sm font-medium mb-3">{_t("Prodotto")}</div>
          <ul className="space-y-2 text-sm text-white/60">
            <li><Link to="/come-funziona" className="hover:text-white">{_t("Come funziona")}</Link></li>
            <li><Link to="/prezzi" className="hover:text-white">{_t("Prezzi")}</Link></li>
            <li><Link to="/faq" className="hover:text-white">{_t("FAQ")}</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-white text-sm font-medium mb-3">{_t("Sicurezza")}</div>
          <ul className="space-y-2 text-sm text-white/60">
            <li><Link to="/sicurezza" className="hover:text-white">{_t("Privacy")}</Link></li>
            <li><Link to="/sicurezza" className="hover:text-white">{_t("Cifratura")}</Link></li>
            <li><Link to="/sicurezza" className="hover:text-white">{_t("GDPR")}</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-white text-sm font-medium mb-3">{_t("Legale")}</div>
          <ul className="space-y-2 text-sm text-white/60">
            <li><Link to="/faq" className="hover:text-white">{_t("Termini")}</Link></li>
            <li><Link to="/sicurezza" className="hover:text-white">{_t("Privacy policy")}</Link></li>
            <li><Link to="/sicurezza" className="hover:text-white">{_t("Cookies")}</Link></li>
          </ul>
        </div>
      </div>
      <div className="text-xs text-white/40">{_t("© {0} Time Capsule · Custodi del tempo", new Date().getFullYear())}</div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen">
      <Nav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Collage fotografico di sfondo */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          {/* Foto principale: donna serena che guarda lontano */}
          <img
            src="/images/collage_1.jpg"
            alt=""
            className="absolute top-0 right-0 w-[58%] h-full object-cover opacity-[0.78]"
            style={{ filter: _t("sepia(0.22) saturate(1.1) contrast(1.02) brightness(0.9)") }}
          />
          {/* Foto secondaria: apertura commossa (in basso a destra, sovrapposta) */}
          <img
            src="/images/collage_2.jpg"
            alt=""
            className="absolute bottom-0 right-[6%] w-[30%] h-[55%] object-cover opacity-[0.6] rounded-sm hidden md:block"
            style={{ filter: _t("sepia(0.3) saturate(1.05) contrast(1.02) brightness(0.9)"), clipPath: _t("polygon(0 12%, 100% 0, 100% 100%, 0 100%)") }}
          />
          {/* Foto dettaglio: mani con la lettera */}
          <img
            src="/images/collage_3.jpg"
            alt=""
            className="absolute top-[12%] left-[52%] w-[22%] h-[45%] object-cover opacity-[0.48] rounded-sm hidden md:block"
            style={{ filter: _t("sepia(0.4) saturate(1.05) contrast(1.02) brightness(0.85)") }}
          />

          {/* Overlay per leggibilità di testo e logo */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1220] via-[#0B1220]/78 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1220]/50 via-transparent to-[#0B1220]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_22%_45%,rgba(11,18,32,0.9)_0%,transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_75%_60%,rgba(255,138,80,0.12),transparent_60%)]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-32 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#FF5722] mb-6 flex items-center gap-2">
              <Sparkles className="w-3 h-3" />{" "}{_t("Il futuro è già qui")}</div>
            <h1 className="font-display text-6xl md:text-7xl lg:text-8xl text-white leading-[1.02] mb-6">
              <em className="text-[#FF5722] rocket-glow not-italic">{_t("Fai viaggiare le tue emozioni nel tempo.")}</em>
            </h1>
            <p className="text-lg text-white/70 max-w-xl mb-10 leading-relaxed">{_t("Time Capsule ti permette di creare capsule digitali con ricordi, emozioni, sogni e documenti da inviare nel futuro. Una volta lanciate, restano custodite fino al giorno che avrai scelto.")}</p>
            <div className="flex flex-wrap gap-4 mb-10">
              <Link to="/register" data-testid="hero-cta" className="btn-launch !text-base !px-8">
                <Rocket className="w-5 h-5 -rotate-45" />{" "}{_t("Crea la tua prima capsula")}</Link>
              <Link to="/come-funziona" className="btn-ghost !text-base">{_t("Come funziona")}{" "}<ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="text-xs text-white/50 flex items-center gap-2">
              <Shield className="w-3.5 h-3.5" />{" "}{_t("Cifrata, sicura, tua per sempre.")}</div>
          </div>

          <div className="relative hidden lg:block" />
        </div>
      </section>

      {/* HOW */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="mb-12 max-w-2xl">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-3">{_t("Come funziona")}</div>
          <h2 className="font-display text-5xl md:text-6xl text-white leading-tight">{_t("Tre passi per parlare col futuro.")}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <Step n="01" title={_t("Prepara")} body={_t("Scegli il tipo di capsula, i destinatari e i contenuti: testi, foto, video, audio, documenti.")} />
          <Step n="02" title={_t("Sigilla")} body={_t("Imposta la data di apertura. Al lancio hai 24 ore per ripensarci, poi la capsula parte.")} />
          <Step n="03" title={_t("Consegna")} body={_t("Il destinatario riceve una notifica quando arriva il momento e apre i tuoi contenuti.")} />
        </div>
      </section>

      {/* Values */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-3 gap-6">
          <Value icon={<Lock className="w-5 h-5" />} title={_t("Sigillata sul serio")} body={_t("Una volta partita, nessuno può aprire, modificare o recuperare la capsula. Neanche noi.")} />
          <Value icon={<Clock className="w-5 h-5" />} title={_t("Precisa nel tempo")} body={_t("Scegli data e ora esatte. Rispettiamo il fuso orario e le transizioni automatiche.")} />
          <Value icon={<Send className="w-5 h-5" />} title={_t("Consegnata a chi ami")} body={_t("Multipli destinatari, notifica al momento giusto, messaggio personale per ciascuno.")} />
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 py-24 text-center">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-4">{_t("Pronto al decollo?")}</div>
        <h2 className="font-display text-5xl md:text-6xl text-white leading-tight mb-6">{_t("I tuoi ricordi sono pronti a viaggiare?")}</h2>
        <Link to="/register" className="btn-launch !text-base !px-8 inline-flex">
          <Rocket className="w-5 h-5 -rotate-45" />{" "}{_t("Inizia ora, è gratis")}</Link>
        <div className="text-xs text-white/40 mt-6">{_t("L'account demo di sviluppo è stato disabilitato. Registrati per esplorare l'app.")}</div>
      </section>

      <Footer />
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="tc-card p-7">
      <div className="font-mono-launch text-[#FF5722] text-lg mb-3">{n}</div>
      <div className="font-display text-2xl text-white mb-2">{title}</div>
      <div className="text-sm text-white/60 leading-relaxed">{body}</div>
    </div>
  );
}

function Value({ icon, title, body }) {
  return (
    <div className="tc-card p-8">
      <div className="w-10 h-10 rounded-full bg-[#FF5722]/15 border border-[#FF5722]/30 flex items-center justify-center text-[#FF5722] mb-5">
        {icon}
      </div>
      <div className="font-display text-2xl text-white mb-2">{title}</div>
      <div className="text-sm text-white/60 leading-relaxed">{body}</div>
    </div>
  );
}
